import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core'
import { includes, filter, values } from 'lodash'
import { StyleLoaderService } from './../core/style-loader/style-loader.service'
import { BreakpointsService } from './../core/breakpoints/breakpoints.service'
import { SeDialogHelper } from './dialog-helper'
import { SeDialogFooterComponent } from './dialog-footer.component'
import {
  SeDialogAction,
  SeDialogActionType,
  SeDialogButton,
  SeDialogOptions,
  SeDialogSize,
  SeDialogVisibility,
} from './dialog-types'

const BASE = 'se-dialog'
const OPEN = `${BASE}--${SeDialogVisibility.open}`
const OPENING = `${BASE}--${SeDialogVisibility.opening}`
const CLOSED = `${BASE}--${SeDialogVisibility.closed}`
const CLOSING = `${BASE}--${SeDialogVisibility.closing}`
const DISABLED = `${BASE}--${SeDialogVisibility.disabled}`

let DIALOG_COUNTER: number = 1
let OPEN_COUNTER: number = 0

@Component({
  standalone: false,
  selector: 'se-dialog',
  templateUrl: './dialog.component.html',
  
})
export class SeDialogComponent implements OnInit, OnDestroy {

  // Exposed for tests
  public buttons: SeDialogButton[] = []
  public size: SeDialogSize = SeDialogSize.medium

  protected isValid: boolean = false
  protected isOpen: boolean = false
  protected enabled: boolean = true
  protected opening: boolean = false
  protected closing: boolean = false
  protected timeout: number
  protected durations: { [key: string]: number } = {}

  @Output('seDataInit') seDataInit: EventEmitter<SeDialogComponent> = new EventEmitter()
  @Output('seDataSubmit') seDataSubmit: EventEmitter<any> = new EventEmitter()

  @HostBinding('id') public id: string = `se-dialog-${DIALOG_COUNTER++}-${Date.now()}`
  @HostBinding('attr.class') public get classAttr(): string {
    return this.classes()
  }

  // @ts-ignore
  @ViewChild(SeDialogFooterComponent, { static: false }) footer: SeDialogFooterComponent

  constructor(
    protected styleLoaderService: StyleLoaderService,
    protected changeDetectorRef: ChangeDetectorRef,
    protected dialogHelper: SeDialogHelper,
  ) {
    // noop
  }

  public ngOnInit(): void {
    this.seDataInit.emit(this)
  }

  public ngOnDestroy(): void {
    const el = this.element()
    if (!el) return   // if dialog was never instantiated, clean up will throw null exception
    const parent = el.parentNode
    if (parent) parent.removeChild(el)
    this.setOpen(false) // makes sure open counter is adjusted if destroyed prior to closing
    this.changeDetectorRef.detach()
  }

  // Manually update options from your custom dialog component
  public setOptions(options: SeDialogOptions): void {
    if (!options) return

    this.setSize(options.size)
    this.setActions(options.actions)
  }

  public setSize(size: SeDialogSize): void {
    const valid = includes(values(SeDialogSize), size)
    this.size = valid ? size : SeDialogSize.medium
  }

  public setActions(actions: SeDialogAction[]): void {
    if (!actions) return

    const actionableActions = filter(actions, action => !!action.action)
    const buttonActions = filter(actionableActions, action => !!action.buttonLabel)
    const primaryActions = filter(buttonActions, action => action.primary || action.destructive)
    const secondaryActions = filter(buttonActions, action => action.secondary)
    const otherActions = filter(buttonActions, action => !(action.primary || action.secondary || action.destructive))
    const actionErrors: string[] = []

    if (primaryActions.length > 1) actionErrors.push('Only one primary or destructive action is allowed')
    if (buttonActions.length === 0) actionErrors.push('You must provide at least one action with a buttonLabel')
    if (actionErrors.length) return actionErrors.forEach(message => this.error(message))

    const visibleActions = [ ...otherActions, ...secondaryActions, ...primaryActions ]

    this.buttons = visibleActions.map(action => this.actionButton(action))
    this.isValid = true
  }

  public open(options?: SeDialogOptions): void {
    if (this.isOpen) return
    if (options) this.setOptions(options)
    if (!this.isValid) return
    this.reset()
    this.setOpen(true)
    this.appendToBody()
    this.animateOpening()
  }

  public close(): void {
    if (!this.isOpen) return
    this.setOpen(false)
    this.footer.cancelSpinner()
    this.animateClosing()
  }

  public update(): void {
    if (!this.changeDetectorRef['destroyed']) {
      this.changeDetectorRef.detectChanges()
    }
  }

  public reset(): void {
    window.clearTimeout(this.timeout)
    this.opening = this.closing = false
    this.footer.reset()
    this.enable()
    this.scrollToTop()
  }

  // Protected methods

  protected duration(): number {
    const durations = this.durations
    return durations.default = durations.default || this.styleLoaderService.getVariable('se-dialog-animation-duration').value
  }

  public classes(): string {
    return `${BASE} ${BASE}--${this.size} ${this.visibilityClasses} ${this.enabled ? '' : DISABLED}`
  }

  protected error(message: string): void {
    console.error(`SeDialog Error: ${message}`)
  }

  protected setOpen(bool: boolean): void {
    if (this.isOpen === bool) return
    const wasOpen = !!OPEN_COUNTER
    OPEN_COUNTER += bool ? 1 : -1
    const isOpen = !!OPEN_COUNTER
    this.isOpen = bool
    if (wasOpen && !isOpen) this.dialogHelper.enableScroll()
    else if (isOpen && !wasOpen) this.dialogHelper.disableScroll()
  }

  // Private methods

  private animateOpening(): void {
    this.opening = true
    this.timeout = window.setTimeout(() => {
      this.opening = false
      this.update()
    }, this.duration())
  }

  private animateClosing(): void {
    this.closing = true
    this.timeout = window.setTimeout(() => {
      this.closing = false
      this.update()
    }, this.duration())
  }

  public async awaitAction(action: Promise<void>|void): Promise<void> {
    this.disable()
    const ret = await action
    this.enable()
    this.seDataSubmit.emit(ret)
    this.footer.reset()
  }

  private disable(): void {
    this.enabled = false
    this.element().addEventListener('keydown', this.disableKeysOtherThanTab, true)
  }

  private enable(): void {
    this.enabled = true
    this.element().removeEventListener('keydown', this.disableKeysOtherThanTab, true)
  }

  private appendToBody(): void {
    document.body.appendChild(this.element())
  }

  private scrollToTop(): void {
    window.setTimeout(() => this.content().scrollTo(0, 0)) // next tick so classes can be applied
  }

  private disableKeysOtherThanTab(e: KeyboardEvent): boolean {
    if (e.key === 'Tab') return
    e.returnValue = false
    return false
  }

  private actionButton(action: SeDialogAction): SeDialogButton {
    const type = this.actionButtonType(action)
    return {
      label: action.buttonLabel,
      action: action.action,
      className: `se-dialog-button--${type}`
    }
  }

  private element(): HTMLElement {
    return document.querySelector(`#${this.id}`)
  }

  private content(): HTMLElement {
    return document.querySelector(`#${this.id} .se-dialog__content`)
  }

  private get visibilityClasses(): string {
    if (this.opening) return `${OPEN} ${OPENING}`
    if (this.closing) return `${OPEN} ${CLOSING}`
    return this.isOpen ? OPEN : CLOSED
  }

  private actionButtonType(action: SeDialogAction): SeDialogActionType {
    if (action.primary) return SeDialogActionType.primary
    if (action.destructive) return SeDialogActionType.destructive
    if (action.secondary) return SeDialogActionType.secondary
    return SeDialogActionType.minimal
  }

}
